import {RegrasDeComponenteKit as C, permiteMultiplaEscolha} from './RegrasDeComponenteKit';
import Decimal from 'decimal.js';

export interface ErroKit { codigo: string; mensagem: string; }

/** Regras puras compartilhadas pelo servidor, loja e PDV. Quantidades são por montagem. */
export class RegrasDeKit {
  static grupo(produto: any): any {
    return (produto.camposAdicionais || []).find((g: any) => g.id === produto.configKit?.adicionalSaboresId);
  }

  static erro(codigo: string, mensagem: string): ErroKit { return { codigo, mensagem }; }

  static configuracao(produto: any): ErroKit | null {
    if (C.grupos(produto).length) {
      const ids = C.grupos(produto).filter(g => g.id != null).map(g => g.id);
      if (new Set(ids).size !== ids.length) return this.erro('KIT_COMPONENTES_INVALIDOS', 'Há grupos duplicados no kit.');
      return C.grupos(produto).map(g => C.configuracao(g)).find(Boolean) || null;
    }
    const c = produto.configKit;
    if (!c || !['FECHADA', 'ESCOLHIDA_PELO_CLIENTE'].includes(c.modalidadeQuantidade))
      return this.erro('KIT_CONFIGURACAO_INVALIDA', 'Escolha a modalidade de quantidade do kit.');
    if (!Number.isSafeInteger(c.qtdeMinimaPorSabor) || c.qtdeMinimaPorSabor < 1 ||
        (c.qtdeMaximaDeSabores != null && (!Number.isSafeInteger(c.qtdeMaximaDeSabores) || c.qtdeMaximaDeSabores < 1)))
      return this.erro('KIT_CONFIGURACAO_INVALIDA', 'Informe um mínimo por sabor e um máximo de sabores válidos.');
    if (c.modalidadeQuantidade === 'FECHADA') {
      if (!produto.tamanhos?.length || produto.tamanhos.some((t: any) =>
        !t.descricao?.trim() || !Number.isSafeInteger(t.qtdeUnidades) || t.qtdeUnidades < c.qtdeMinimaPorSabor ||
        !Number.isFinite(t.preco) || t.preco < 0 || (t.novoPreco != null && (!Number.isFinite(t.novoPreco) || t.novoPreco < 0))))
        return this.erro('KIT_CONFIGURACAO_INVALIDA', 'Cadastre quantidades com nome, unidades e preço válidos.');
    } else {
      if (![c.qtdeMinimaCompra, c.incrementoCompra, c.qtdeUnidadesReferencia].every(v => Number.isSafeInteger(v) && v > 0) ||
          c.qtdeMinimaCompra < c.qtdeMinimaPorSabor ||
          (c.qtdeMaximaCompra != null && (!Number.isSafeInteger(c.qtdeMaximaCompra) || c.qtdeMaximaCompra < c.qtdeMinimaCompra)) ||
          !Number.isFinite(produto.preco) || produto.preco < 0 ||
          (produto.novoPreco != null && (!Number.isFinite(produto.novoPreco) || produto.novoPreco < 0)))
        return this.erro('KIT_CONFIGURACAO_INVALIDA', 'Verifique mínimo, máximo, incremento e preço de referência do kit.');
    }
    return null;
  }

  static unidades(produto: any, unidades: number, tamanho: any): ErroKit | null {
    const c = produto.configKit;
    if (!c) return this.erro('KIT_CONFIGURACAO_INVALIDA', 'Kit sem configuração de quantidade.');
    if (c.modalidadeQuantidade === 'FECHADA') {
      const escolhido = (produto.tamanhos || []).find((t: any) => t.id === tamanho?.id);
      if (!escolhido || escolhido.disponivel === false)
        return this.erro('KIT_QUANTIDADE_OBRIGATORIA', 'Escolha uma quantidade disponível deste produto.');
      if (unidades !== escolhido.qtdeUnidades)
        return this.erro('KIT_UNIDADES_INVALIDAS', 'As unidades devem corresponder à quantidade escolhida.');
    } else {
      if (tamanho) return this.erro('KIT_UNIDADES_INVALIDAS', 'Informe as unidades da montagem, sem selecionar um tamanho.');
      if (unidades == null) return this.erro('KIT_UNIDADES_OBRIGATORIAS', 'Informe quantas unidades você quer comprar.');
      if (!Number.isSafeInteger(unidades) || unidades < 1)
        return this.erro('KIT_UNIDADES_INVALIDAS', 'Informe uma quantidade inteira e positiva.');
      if (unidades < c.qtdeMinimaCompra)
        return this.erro('KIT_MINIMO_COMPRA', `O mínimo deste produto é de ${c.qtdeMinimaCompra} unidades.`);
      if (c.qtdeMaximaCompra != null && unidades > c.qtdeMaximaCompra)
        return this.erro('KIT_MAXIMO_COMPRA', `Cada montagem permite no máximo ${c.qtdeMaximaCompra} unidades. Você informou ${unidades}.`);
      if ((unidades - c.qtdeMinimaCompra) % c.incrementoCompra !== 0)
        return this.erro('KIT_INCREMENTO_COMPRA', `Escolha de ${c.incrementoCompra} em ${c.incrementoCompra} a partir de ${c.qtdeMinimaCompra} unidades.`);
    }
    return null;
  }

  static distribuicao(produto: any, unidades: number, escolhas: { opcao: any; qtde: number }[], grupo: any): ErroKit | null {
    if (!grupo) return this.erro('KIT_SABORES_OBRIGATORIOS', 'Escolha os sabores do kit.');
    const ids = new Set<number>();
    let soma = 0;
    for (const escolha of escolhas) {
      const opcao = (grupo.opcoesDisponiveis || []).find((o: any) => o.id === escolha.opcao?.id);
      if (!opcao || opcao.disponivel === false || opcao.esconder || opcao.oculta || ids.has(opcao.id))
        return this.erro('KIT_SABOR_INDISPONIVEL', 'Escolha sabores disponíveis, sem duplicar opções.');
      ids.add(opcao.id);
      if (!Number.isSafeInteger(escolha.qtde) || escolha.qtde < 1)
        return this.erro('KIT_UNIDADES_INVALIDAS', 'As quantidades dos sabores devem ser inteiras e positivas.');
      const minimo = Math.max(produto.configKit.qtdeMinimaPorSabor, opcao.qtdeMinima || 1);
      if (escolha.qtde < minimo)
        return this.erro('KIT_MINIMO_POR_SABOR', `${opcao.nome} precisa ter pelo menos ${minimo} unidades.`);
      soma += escolha.qtde;
    }
    if (produto.configKit.qtdeMaximaDeSabores != null && ids.size > produto.configKit.qtdeMaximaDeSabores)
      return this.erro('KIT_MAXIMO_DE_SABORES', `Escolha até ${produto.configKit.qtdeMaximaDeSabores} sabores.`);
    if (soma !== unidades)
      return this.erro('KIT_SOMA_DIFERENTE', `${soma < unidades ? 'Faltam' : 'Sobram'} ${Math.abs(unidades - soma)} unidades para completar ${unidades}.`);
    return null;
  }

  static erroDoItemTela(item: any): ErroKit | null {
    if (item.produto?.tipo !== 'kit') return null;
    if (C.grupos(item.produto).length) return C.valideItem({...item, componentesKit: item.componentesKit || item.adicionais?.componentesKit});
    const erro = this.unidades(item.produto, item.qtdeUnidadesKit, item.produtoTamanho);
    if (erro) return erro;
    const grupo = this.grupo(item.produto);
    const indice = (item.produto.camposAdicionais || []).filter((g: any) => permiteMultiplaEscolha(g)).indexOf(grupo);
    const lista = item.adicionais?.['lista' + indice] || {};
    const escolhas: any[] = Object.keys(lista).filter(k => k.startsWith('opcao_') && lista[k]?.selecionada).map(k => lista[k]);
    return this.distribuicao(item.produto, item.qtdeUnidadesKit, escolhas, grupo);
  }

  static precoBase(produto: any, unidades: number, tamanho?: any, componentes?: any[]): number {
    if (C.grupos(produto).length) return C.precoBase(produto, componentes || []);
    if (!produto.configKit) return 0;
    const alvo = produto.configKit.modalidadeQuantidade === 'FECHADA' ? tamanho : produto;
    if (!alvo || unidades == null) return 0; // Prévia enquanto o cliente ainda escolhe.
    const preco = alvo.novoPreco != null ? alvo.novoPreco : alvo.preco;
    const valor = new Decimal(preco || 0);
    return (produto.configKit.modalidadeQuantidade === 'FECHADA' ? valor :
      valor.mul(unidades).div(produto.configKit.qtdeUnidadesReferencia)).toDecimalPlaces(2).toNumber();
  }
}
